'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Breadcrumbs, Header, useCart } from '@/components';
import ArrowNarrowRightIcon from '@/components/icons/ArrowNarrowRightIcon';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';
const REQUEST_TIMEOUT_MS = 3500;

type Collection = {
  id: string;
  title: string;
  handle: string;
  description: string | null;
  bannerImageUrl: string | null;
  /** Shoppable (in-stock, grouped) products at the selected location. */
  productCount: number;
};

type Store = {
  covaLocationId: number;
  name: string;
};

function fallbackCollectionImage(index: number): string {
  return `/media/sections/blogs/articles/${(index % 6) + 1}.webp`;
}

async function fetchStores() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/stores`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Failed to load stores: ${res.status}`);
    return res.json();
  } catch {
    return { stores: [] } as { stores: Store[] };
  } finally {
    clearTimeout(timeoutId);
  }
}

export default function CollectionsPage() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const { state } = useCart();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const storesData = await fetchStores();
        setStores(storesData?.stores || []);

        const params = new URLSearchParams();
        if (state.fulfillmentMode === 'pickup' && state.storeCovaLocationId) {
          params.set('covaLocationId', String(state.storeCovaLocationId));
          params.set('locationType', 'store');
        } else if (state.fulfillmentMode === 'delivery') {
          params.set('locationType', 'same-day');
          if (state.storeCovaLocationId) {
            params.set('covaLocationId', String(state.storeCovaLocationId));
          }
        }

        const url = `${API_BASE}/api/collections/public${params.toString() ? `?${params.toString()}` : ''}`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
          const res = await fetch(url, { signal: controller.signal });
          const data = await res.json();
          if (data?.success) {
            // Empty collections are dead ends for shoppers; keep them admin-only.
            setCollections(
              (data.collections || []).filter(
                (collection: Collection) => (collection.productCount ?? 0) > 0
              )
            );
          }
        } finally {
          clearTimeout(timeoutId);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [state.storeCovaLocationId, state.fulfillmentMode]);

  return (
    <div className="min-h-screen">
      <Header stores={stores} base={API_BASE} />
      <main className="home-page w-full max-w-none px-4 pb-16 pt-4 sm:px-8 sm:pt-6">
        {/* Hero band, same treatment as the /search shop hero */}
        <div className="relative overflow-hidden rounded-xl bg-brand-100 sm:rounded-2xl">
          <div className="absolute right-0 top-0 hidden h-full w-1/2 lg:block">
            <Image
              src="/media/categories/covers/cover-all.webp"
              alt=""
              fill
              sizes="(min-width: 1024px) 50vw, 100vw"
              className="object-cover object-right"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(23, 22, 90, 0) 0%, #17165A 111.31%)' }}
            />
          </div>
          <div className="absolute inset-0 lg:hidden">
            <Image
              src="/media/categories/covers/cover-all.webp"
              alt=""
              fill
              sizes="100vw"
              className="object-cover object-right"
            />
            <div className="absolute inset-0 bg-brand-100/80" />
          </div>

          <div className="relative p-4 lg:p-6">
            <Breadcrumbs items={[{ label: 'Collections' }]} tone="onDark" className="mb-0" />
            <h1 className="mt-3 text-h3-mobile font-bold text-constant-white lg:text-h3-desktop">
              Shop by collection
            </h1>
            <p className="mt-2 max-w-3xl text-body-14-mobile font-medium text-constant-white/80 lg:text-body-16">
              Curated edits of our menu — staff picks, best sellers, fresh drops, and deals — each
              collection hand-built to make finding your next favourite faster.
            </p>
            {!loading && collections.length > 0 && (
              <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-constant-white/60">
                {collections.length} collection{collections.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
        </div>

        <section className="mt-6 w-full">
          {loading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`collection-skeleton-${index}`}
                  className="h-[260px] w-full animate-pulse rounded-2xl border border-neutral-10 bg-neutral-5"
                />
              ))}
            </div>
          ) : collections.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {collections.map((collection, index) => {
                const href = `/collections/${collection.handle}`;
                // Relative /uploads paths stay relative: they proxy through the
                // client origin (next.config rewrite) so next/image can fetch them.
                const src = collection.bannerImageUrl || fallbackCollectionImage(index);

                return (
                  <Link
                    key={collection.id}
                    href={href}
                    aria-label={`Open collection: ${collection.title}`}
                    className="group relative block h-[260px] w-full overflow-hidden rounded-2xl border border-neutral-10"
                  >
                    <Image
                      src={src}
                      alt=""
                      fill
                      className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    {/* Legibility gradient, heavier at the caption edge */}
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background:
                          'linear-gradient(180deg, rgba(23,22,90,0.15) 0%, rgba(23,22,90,0.25) 55%, rgba(23,22,90,0.78) 100%)',
                      }}
                    />

                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                      <div className="min-w-0">
                        <h2 className="truncate text-body-20 font-bold leading-snug text-constant-white">
                          {collection.title}
                        </h2>
                        <p className="mt-0.5 text-xs font-medium text-constant-white/75">
                          {collection.productCount} product{collection.productCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <span
                        aria-hidden
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-constant-white/30 bg-constant-white/15 text-constant-white backdrop-blur-sm transition-colors duration-200 group-hover:border-constant-white group-hover:bg-constant-white group-hover:text-blue-100"
                      >
                        <ArrowNarrowRightIcon size={18} color="currentColor" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-neutral-10 bg-constant-white px-6 py-14 text-center shadow-sm">
              <p className="text-body-16 font-semibold text-text-primary">No collections here yet</p>
              <p className="mx-auto mt-2 max-w-md text-body-14-desktop text-text-secondary">
                No collections are available for your selected location. Browse the full menu instead.
              </p>
              <Link
                href="/search"
                className="mt-6 inline-flex h-11 items-center justify-center rounded-xl border border-blue-100 bg-blue-100 px-6 text-sm font-semibold text-constant-white transition-colors hover:bg-blue-80"
              >
                Shop all products
              </Link>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
